'use client';

import React from 'react';
import { Box, Typography, Button, Dialog } from 'src/components';
import { useDeleteAdvancedSearchMutation } from 'src/api/redux/advancedSearches';
import { useNotification } from 'src/context/notification';

interface DeleteAISearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  volumeToDelete: {
    id: number;
    label: string;
  } | null;
  onDeleteSuccess?: () => void;
}

const DeleteAISearchDialog: React.FC<DeleteAISearchDialogProps> = ({
  isOpen,
  onClose,
  volumeToDelete,
  onDeleteSuccess,
}) => {
  const { setNotification } = useNotification();
  const [deleteAdvancedSearch, { isLoading: isDeleting }] =
    useDeleteAdvancedSearchMutation();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!volumeToDelete) return;

    try {
      await deleteAdvancedSearch(volumeToDelete.id).unwrap();

      setNotification({
        title: 'Success',
        message: `AI search "${volumeToDelete.label}" has been deleted`,
        type: 'success',
      });

      if (onDeleteSuccess) {
        onDeleteSuccess();
      }

      onClose();
    } catch (error: any) {
      setNotification({
        title: 'Error',
        message:
          error?.data?.error_message ||
          'Failed to delete AI search. Please try again.',
        type: 'error',
      });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="delete-ai-search-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            data-testid="delete-ai-search-dialog-title"
            className="text-neutral-800 mb-4"
          >
            Are you sure you want to delete AI search &quot;
            {volumeToDelete?.label}&quot;? This action cannot be undone.
          </Typography>
          <Box className="flex justify-end gap-3 mt-6">
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="px-4"
              data-testid="delete-ai-search-dialog-cancel-button"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDelete}
              className="px-4"
              data-testid="delete-ai-search-dialog-confirm-button"
              loading={isDeleting}
              disabled={isDeleting}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeleteAISearchDialog;
